import { collection, doc, endBefore, getCountFromServer, getDocs, limit, limitToLast, or, and, orderBy, query, runTransaction, startAfter, where, writeBatch } from 'firebase/firestore';
import { orderBy as lodashOrderBy, toLower } from 'lodash';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { createBlindWithId, getAll, getByDoubleQuery, getByDoubleQueryWithList, getById, getByIdQuery, getBySingleQuery, getCreateFields, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { DEFAULT_ACCOUNT_TIME_ZONE, getTimeZoneForAddresses, toAccountTimeZone } from '../utils/timeZoneUtils';
import { db } from '../firebase';
import AuthUser from './AuthUser';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import UserInsurance from './UserInsurance';
import Address from './Address';
import CoveredEntityUser from './CoveredEntityUser';
import { v4 as uuid } from 'uuid';
import { addUserFile, getFileExt, getFileUrl } from '../firebase/StorageService';
import ValueHistory from './ValueHistory';
import WeightSnapshot from './WeightSnapshot';
import Subscription from './Subscription';
import Order from './Order';
import AffiliateUser from './AffiliateUser';
import Encounter from './Encounter';
import Prescription from './Prescription';
import { fromUtcToDate } from '../utils/dateFormatter';
import Program from './Program';
import { getProgramNameById, getProgramSlugById } from '../utils/entityUtils';
import { ROLE_FLAGS, toRoleFlags } from '../utils/roleFlagUtils';
import moment from 'moment-timezone';
import PartnerUser from './PartnerUser';

// what setauthuserclaims answers for an account that has no login
const NO_LOGIN_ERROR_CODE = 'functions/not-found';

class User {
	static collectionName = 'users';

	userId = null;
	patientId = null;
	userRefId = null;
	externalRefs = {}; // external patient refs keyed by pharmacyId; per-system so a customer served by multiple pharmacies doesn't overwrite one another

	email = null;
	phone = null;
	timeZone = null; // the account's time zone for every channel, an ACCOUNT_TIME_ZONES value such as 'US/Eastern'

	firstName = null;
	firstNameLower = null;
	middleName = null;
	lastName = null;
	lastNameLower = null;
	suffix = null;

	// null, not 'UNKNOWN': every other model here defaults gender to null, and a placeholder that
	// reads as a value is worse than an absent one - it satisfies isStringNotEmpty, survives every
	// copy into Customer, Lead and the encounter, and reaches a prescriber as a stated fact rather
	// than a gap. every outbound mapper folds it into the same branch as null anyway
	gender = null;
	dateOfBirth = null;
	race = 'UNKNOWN';
	ethnicity = 'UNKNOWN';

	height = 0; // in inches
	weight = 0; // current weight in lbs
	weightHistory = []; // list of ValueHistory, historical changes to weight (recent window; full history in the weightHistory subcollection)
	weightSnapshot = new WeightSnapshot(null).toDto(); // starting/current/previous/lowest/highest weight with dates
	bmi = 0.0;

	socialSecurityNumber = null;

	billingAddress = new Address(null).toDto();
	shippingAddress = new Address(null).toDto();

	isRep = false;
	isAdmin = false;
	isSuperAdmin = false;
	isCoveredEntityUser = false;
	isAffiliateUser = false;
	isPartnerUser = false;
	isHelpStaff = false;
	isStaffManager = false;
	isMarketingManager = false;
	isOwner = false;
	isDesigner = false; // funnel authoring only; never staff, so it opens no patient data

	notes = [];
	notesCount = 0;
	insurances = []; // array of Insurance
	insurancesCount = 0;
	documents = [];
	documentsCount = 0;
	agreements = [];
	agreementsCount = 0;
	forms = []; // array of UserForm
	formsCount = 0;

	phoneVerified = false;

	activityCount = 0;
	smsCount = 0;
	emailCount = 0;
	faceCount = 0;
	callCount = 0;

	imageRefId = "";
	#image = null;

	medications = [];
	medicationsHistory = []; // list of ValueHistory, historical changes to medications

	allergies = [];
	allergiesHistory = []; // list of ValueHistory, historical changes to allergies

	medicalConditions = [];
	medicalConditionsHistory = []; // list of ValueHistory, historical changes to medicalConditions

	isFriendsAndFamily = false;
	couponUses = [];
	referralCode = null;
	preferredContactMethod = null;
	callConsent = {
		// current authorization to place marketing or sales calls
		canCall: true,

		canLeaveVoicemail: true,
		bestCallWindow: [],

		consentAt: null,
		consentSource: null,

		revokedAt: null,
		revokedSource: null,
		revokedReason: null
	};
	// marketing email and sms default on: the terms and privacy policy accepted at account creation cover marketing
	communicationPreferences = {
		// global marketing preference - when true, overrides every channel-specific marketing preference
		marketingOptOut: false,
		marketingOptOutAt: null,
		marketingOptOutSource: null,
		marketingOptOutReason: null,

		// email marketing
		marketingEmails: true,
		marketingEmailConsentAt: null,
		marketingEmailConsentSource: null,
		marketingEmailUnsubscribedAt: null,
		marketingEmailUnsubscribeSource: null,
		marketingEmailUnsubscribeReason: null,

		// a spam report stays on record after marketing email is turned back on and sendgrid's list is cleared
		emailSpamReported: false,
		emailSpamReportedAt: null,

		// sms marketing
		marketingSms: true,
		marketingSmsConsentAt: null,
		marketingSmsConsentSource: null,
		marketingSmsUnsubscribedAt: null,
		marketingSmsUnsubscribeSource: null,
		marketingSmsUnsubscribeReason: null,

		// the transactional number's STOP state at the carrier, distinct from an ordinary sms marketing unsubscribe
		smsOptOut: false,
		smsOptOutAt: null,
		smsOptOutSource: null,
		smsOptOutReason: null,

		// records a validated START or equivalent provider resubscribe event
		smsOptInAt: null,
		smsOptInSource: null,

		// the marketing number's STOP state - the carrier blocks each number separately
		marketingNumberOptOut: false,
		marketingNumberOptOutAt: null,
		marketingNumberOptOutSource: null,
		marketingNumberOptOutReason: null,
		marketingNumberOptInAt: null,
		marketingNumberOptInSource: null,

		// push marketing
		marketingPush: false,
		marketingPushConsentAt: null,
		marketingPushConsentSource: null,
		marketingPushUnsubscribedAt: null,
		marketingPushUnsubscribeSource: null,
		marketingPushUnsubscribeReason: null,

		// required transactional preferences - these remain true and are read-only in the patient ui
		orderUpdates: true,
		prescriptionAlerts: true,
		billingNotifications: true,
		appointmentReminders: true,

		// existing compatibility fields - if support communication is always required, display as read-only
		supportEmails: true,
		supportSms: true,

		preferredContactMethod: 'EMAIL',

		// quiet hours by channel, as hh:mm in the account's timeZone, running from start across midnight to end.
		// texts, calls and push wait out 20:00-09:00, inside the federal 8am-9pm window and the 8am-8pm some states
		// set; email goes at any hour
		emailQuietHoursStart: null,
		emailQuietHoursEnd: null,

		smsQuietHoursStart: '20:00',
		smsQuietHoursEnd: '09:00',

		callQuietHoursStart: '20:00',
		callQuietHoursEnd: '09:00',

		pushQuietHoursStart: '20:00',
		pushQuietHoursEnd: '09:00'
	};
	supportCategories = []; // Array of TaskCategory values this rep specializes in
	supportRoutingStatus = 'AVAILABLE';
	supportMaxOpenTickets = null;
	supportRoutingPaused = false;

	repId = 'NONE'; // userId of customer representative

	taskCount = 0;

	// Customer Analytics & Metadata
	totalSubscriptions = 0;
	activeSubscriptions = 0;
	cancelledSubscriptions = 0;
	pendingSubscriptions = 0;
	onHoldSubscriptions = 0;
	activePrograms = []; // array of program slugs user is actively enrolled in
	cancelledPrograms = []; // array of program slugs user has cancelled
	pendingPrograms = [];
	onHoldPrograms = [];
	activeProducts = []; // array of product SKUs currently active
	cancelledProducts = []; // array of product SKUs previously cancelled
	pendingProducts = [];
	onHoldProducts = [];
	totalLifetimeValue = 0.0; // total amount spent by customer
	averageOrderValue = 0.0;
	totalOrderCount = 0;
	customerSince = null; // date when customer first signed up
	customerSinceDays = null; // calculated days since customer first signed up
	daysSinceFirstOrder = null; // null instead of 0
	daysSinceLastOrder = null; // null instead of 0
	lastOrderDate = null;
	firstOrderDate = null;
	churnRisk = 'LOW'; // LOW, MEDIUM, HIGH
	customerSegment = 'LEAD'; // STANDARD, VIP, HIGH_VALUE, AT_RISK, LEAD, LOST, etc.
	lastEngagementDate = null;
	engagementScore = 0; // calculated score based on activity

	// New status tracking fields
	hasPastDuePayments = false;
	hasMissedEncounters = false;
	hasDelayedShipments = false;
	needsCall = false;
	needsCallCount = 0;
	nextCallActionAt = null;

	newDashboardTutorialStatus = 'UNSEEN';

	metadata = {}; // can store customerIo: { isSynced, syncedAt } - set once customer.io confirms the first create

	constructor(user) {
		if (isObjectNotEmpty(user)) {
			if (isStringNotEmpty(user.userId)) { this.userId = user.userId; }
			if (isStringNotEmpty(user.patientId)) { this.patientId = user.patientId; }
			if (isStringNotEmpty(user.userRefId)) { this.userRefId = user.userRefId; }
			if (isObjectNotEmpty(user.externalRefs)) { this.externalRefs = user.externalRefs; }

			if (isStringNotEmpty(user.email)) { this.email = toLower(user.email); }
			if (isStringNotEmpty(user.phone)) { this.phone = toE164PhoneNumber(user.phone); }
			if (isStringNotEmpty(user.timeZone)) { this.timeZone = user.timeZone; }

			if (isStringNotEmpty(user.firstName)) {
				this.firstName = user.firstName;
				this.firstNameLower = toLower(user.firstName);
			}
			if (isStringNotEmpty(user.lastName)) {
				this.lastName = user.lastName;
				this.lastNameLower = toLower(user.lastName);
			}
			if (isStringNotEmpty(user.middleName)) { this.middleName = user.middleName; }
			if (isStringNotEmpty(user.suffix)) { this.suffix = user.suffix; }

			if (isStringNotEmpty(user.gender)) { this.gender = user.gender; }
			if (isStringNotEmpty(user.dateOfBirth)) { this.dateOfBirth = user.dateOfBirth; }
			if (isStringNotEmpty(user.race)) { this.race = user.race; }
			if (isStringNotEmpty(user.ethnicity)) { this.ethnicity = user.ethnicity; }

			if (isNumberNotEmpty(user.height)) { this.height = user.height; }
			if (isNumberNotEmpty(user.weight)) { this.weight = user.weight; }
			if (isListNotEmpty(user.weightHistory)) { this.weightHistory = user.weightHistory; }
			// no seed from the weight: readings are recorded through recordweightchange, and a document
			// with a weight but no history is exactly what a rebuilt snapshot must not invent one for
			if (isNumberGreaterThanZero(user.weightSnapshot?.startingWeight)) { this.weightSnapshot = new WeightSnapshot(user.weightSnapshot).toDto(); }
			else if (isListNotEmpty(this.weightHistory)) { this.weightSnapshot = WeightSnapshot.buildFromHistory(this.weightHistory); }
			if (isNumberNotEmpty(user.bmi)) { this.bmi = user.bmi; }

			if (isStringNotEmpty(user.socialSecurityNumber)) { this.socialSecurityNumber = user.socialSecurityNumber; }

			if (isObjectNotEmpty(user.billingAddress)) { this.billingAddress = new Address(user.billingAddress).toDto(); }
			if (isObjectNotEmpty(user.shippingAddress)) { this.shippingAddress = new Address(user.shippingAddress).toDto(); }

			if (isBooleanTrue(user.isRep)) { this.isRep = true; }
			if (isBooleanTrue(user.isAdmin)) { this.isAdmin = true; }
			if (isBooleanTrue(user.isSuperAdmin)) { this.isSuperAdmin = true; }
			if (isBooleanTrue(user.isCoveredEntityUser)) { this.isCoveredEntityUser = true; }
			if (isBooleanTrue(user.isAffiliateUser)) { this.isAffiliateUser = true; }
			if (isBooleanTrue(user.isPartnerUser)) { this.isPartnerUser = true; }
			if (isBooleanTrue(user.isHelpStaff)) { this.isHelpStaff = true; }
			if (isBooleanTrue(user.isStaffManager)) { this.isStaffManager = true; }
			if (isBooleanTrue(user.isMarketingManager)) { this.isMarketingManager = true; }
			if (isBooleanTrue(user.isOwner)) { this.isOwner = true; }
			if (isBooleanTrue(user.isDesigner)) { this.isDesigner = true; }

			if (isListNotEmpty(user.notes)) {
				this.notes = user.notes;
				this.notesCount = user.notes.length;
			}
			if (isListNotEmpty(user.insurances)) {
				this.insurances = user.insurances;
				this.insurancesCount = user.insurances.length;
			}
			if (isListNotEmpty(user.documents)) {
				this.documents = user.documents;
				this.documentsCount = user.documents.length;
			}
			if (isListNotEmpty(user.agreements)) {
				this.agreements = user.agreements;
				this.agreementsCount = user.agreements.length;
			}
			if (isListNotEmpty(user.forms)) {
				this.forms = user.forms;
				this.formsCount = user.forms.length;
			}

			if (isBooleanTrue(user.phoneVerified)) { this.phoneVerified = true; }

			if (isNumberGreaterThanOrEqualToZero(user.activityCount)) { this.activityCount = user.activityCount; }
			if (isNumberGreaterThanOrEqualToZero(user.smsCount)) { this.smsCount = user.smsCount; }
			if (isNumberGreaterThanOrEqualToZero(user.emailCount)) { this.emailCount = user.emailCount; }
			if (isNumberGreaterThanOrEqualToZero(user.faceCount)) { this.faceCount = user.faceCount; }
			if (isNumberGreaterThanOrEqualToZero(user.callCount)) { this.callCount = user.callCount; }

			if (isNotNullOrUndefined(user.image)) { this.#image = user.image; }
			if (isStringNotEmpty(user.imageRefId)) { this.imageRefId = user.imageRefId; }


			if (isListNotEmpty(user.medications)) { this.medications = user.medications; }
			if (isListNotEmpty(user.medicationsHistory)) { this.medicationsHistory = user.medicationsHistory; }
			else if (isListNotEmpty(this.medications)) { this.medicationsHistory = ValueHistory.addNewValue(this.medications); }


			if (isListNotEmpty(user.allergies)) { this.allergies = user.allergies; }
			if (isListNotEmpty(user.allergiesHistory)) { this.allergiesHistory = user.allergiesHistory; }
			else if (isListNotEmpty(this.allergies)) { this.allergiesHistory = ValueHistory.addNewValue(this.allergies); }

			if (isListNotEmpty(user.medicalConditions)) { this.medicalConditions = user.medicalConditions; }
			if (isListNotEmpty(user.medicalConditionsHistory)) { this.medicalConditionsHistory = user.medicalConditionsHistory; }
			else if (isListNotEmpty(this.medicalConditions)) { this.medicalConditionsHistory = ValueHistory.addNewValue(this.medicalConditions); }

			if (isBooleanTrue(user.isFriendsAndFamily)) { this.isFriendsAndFamily = true; }
			if (isListNotEmpty(user.couponUses)) { this.couponUses = user.couponUses; }
			if (isStringNotEmpty(user.referralCode)) { this.referralCode = user.referralCode; }
			if (isStringNotEmpty(user.preferredContactMethod)) { this.preferredContactMethod = user.preferredContactMethod; }
			if (isObjectNotEmpty(user.callConsent)) {
				const consent = user.callConsent;
				if (isBooleanTrue(consent.canCall)) { this.callConsent.canCall = true; }
				else { this.callConsent.canCall = false; }
				if (isBooleanTrue(consent.canLeaveVoicemail)) { this.callConsent.canLeaveVoicemail = true; }
				else { this.callConsent.canLeaveVoicemail = false; }
				if (isListNotEmpty(consent.bestCallWindow)) { this.callConsent.bestCallWindow = [...consent.bestCallWindow]; }
				else { this.callConsent.bestCallWindow = []; }
				if (isNotNullOrUndefined(consent.consentAt)) { this.callConsent.consentAt = consent.consentAt; }
				if (isStringNotEmpty(consent.consentSource)) { this.callConsent.consentSource = consent.consentSource; }
				if (isNotNullOrUndefined(consent.revokedAt)) { this.callConsent.revokedAt = consent.revokedAt; }
				if (isStringNotEmpty(consent.revokedSource)) { this.callConsent.revokedSource = consent.revokedSource; }
				if (isStringNotEmpty(consent.revokedReason)) { this.callConsent.revokedReason = consent.revokedReason; }
			}
			if (isObjectNotEmpty(user.communicationPreferences)) { this.communicationPreferences = { ...this.communicationPreferences, ...user.communicationPreferences }; }
			if (isListNotEmpty(user.supportCategories)) { this.supportCategories = user.supportCategories; }
			if (isStringNotEmpty(user.supportRoutingStatus)) { this.supportRoutingStatus = user.supportRoutingStatus; }
			if (isNumberGreaterThanOrEqualToZero(user.supportMaxOpenTickets)) { this.supportMaxOpenTickets = user.supportMaxOpenTickets; }
			if (isBooleanTrue(user.supportRoutingPaused)) { this.supportRoutingPaused = true; }

			if (isStringNotEmpty(user.repId)) { this.repId = user.repId; }

			if (isNumberGreaterThanOrEqualToZero(user.taskCount)) { this.taskCount = user.taskCount; }

			// Customer Analytics & Metadata
			if (isNumberGreaterThanOrEqualToZero(user.totalSubscriptions)) { this.totalSubscriptions = user.totalSubscriptions; }
			if (isNumberGreaterThanOrEqualToZero(user.activeSubscriptions)) { this.activeSubscriptions = user.activeSubscriptions; }
			if (isNumberGreaterThanOrEqualToZero(user.cancelledSubscriptions)) { this.cancelledSubscriptions = user.cancelledSubscriptions; }
			if (isNumberGreaterThanOrEqualToZero(user.pendingSubscriptions)) { this.pendingSubscriptions = user.pendingSubscriptions; }
			if (isNumberGreaterThanOrEqualToZero(user.onHoldSubscriptions)) { this.onHoldSubscriptions = user.onHoldSubscriptions; }
			if (isListNotEmpty(user.activePrograms)) { this.activePrograms = user.activePrograms; }
			if (isListNotEmpty(user.cancelledPrograms)) { this.cancelledPrograms = user.cancelledPrograms; }
			if (isListNotEmpty(user.pendingPrograms)) { this.pendingPrograms = user.pendingPrograms; }
			if (isListNotEmpty(user.onHoldPrograms)) { this.onHoldPrograms = user.onHoldPrograms; }
			if (isListNotEmpty(user.activeProducts)) { this.activeProducts = user.activeProducts; }
			if (isListNotEmpty(user.cancelledProducts)) { this.cancelledProducts = user.cancelledProducts; }
			if (isListNotEmpty(user.pendingProducts)) { this.pendingProducts = user.pendingProducts; }
			if (isListNotEmpty(user.onHoldProducts)) { this.onHoldProducts = user.onHoldProducts; }
			if (isNumberGreaterThanOrEqualToZero(user.totalLifetimeValue)) { this.totalLifetimeValue = user.totalLifetimeValue; }
			if (isNumberGreaterThanOrEqualToZero(user.averageOrderValue)) { this.averageOrderValue = user.averageOrderValue; }
			if (isNumberGreaterThanOrEqualToZero(user.totalOrderCount)) { this.totalOrderCount = user.totalOrderCount; }

			if (isNotNullOrUndefined(user.customerSince)) { this.customerSince = user.customerSince; }
			if (isNotNullOrUndefined(user.customerSinceDays)) { this.customerSinceDays = user.customerSinceDays; }
			if (isNotNullOrUndefined(user.daysSinceFirstOrder)) { this.daysSinceFirstOrder = user.daysSinceFirstOrder; }
			if (isNotNullOrUndefined(user.daysSinceLastOrder)) { this.daysSinceLastOrder = user.daysSinceLastOrder; }
			if (isNotNullOrUndefined(user.lastOrderDate)) { this.lastOrderDate = user.lastOrderDate; }
			if (isNotNullOrUndefined(user.firstOrderDate)) { this.firstOrderDate = user.firstOrderDate; }
			if (isStringNotEmpty(user.churnRisk)) { this.churnRisk = user.churnRisk; }
			if (isStringNotEmpty(user.customerSegment)) { this.customerSegment = user.customerSegment; }
			if (isNotNullOrUndefined(user.lastEngagementDate)) { this.lastEngagementDate = user.lastEngagementDate; }
			if (isNumberGreaterThanOrEqualToZero(user.engagementScore)) { this.engagementScore = user.engagementScore; }

			// New status tracking fields
			if (isBooleanTrue(user.hasPastDuePayments)) { this.hasPastDuePayments = user.hasPastDuePayments; }
			if (isBooleanTrue(user.hasMissedEncounters)) { this.hasMissedEncounters = user.hasMissedEncounters; }
			if (isBooleanTrue(user.hasDelayedShipments)) { this.hasDelayedShipments = user.hasDelayedShipments; }
			if (isBooleanTrue(user.needsCall)) { this.needsCall = true; }
			if (isNumberGreaterThanOrEqualToZero(user.needsCallCount)) { this.needsCallCount = user.needsCallCount; }
			if (isNotNullOrUndefined(user.nextCallActionAt)) { this.nextCallActionAt = user.nextCallActionAt; }
			if (isStringNotEmpty(user.newDashboardTutorialStatus)) { this.newDashboardTutorialStatus = user.newDashboardTutorialStatus; }

			if (isObjectNotEmpty(user.metadata)) { this.metadata = user.metadata; }
		}
	}

	toDto = () => {
		return {
			userId: this.userId,
			patientId: this.patientId,
			userRefId: this.userRefId,
			externalRefs: this.externalRefs,

			email: this.email,
			phone: this.phone,
			timeZone: this.timeZone,

			firstName: this.firstName,
			firstNameLower: this.firstNameLower,
			middleName: this.middleName,
			lastName: this.lastName,
			lastNameLower: this.lastNameLower,
			suffix: this.suffix,

			gender: this.gender,
			dateOfBirth: this.dateOfBirth,
			race: this.race,
			ethnicity: this.ethnicity,

			height: this.height,
			weight: this.weight,
			weightHistory: this.weightHistory,
			weightSnapshot: this.weightSnapshot,
			bmi: this.bmi,

			socialSecurityNumber: this.socialSecurityNumber,

			billingAddress: this.billingAddress,
			shippingAddress: this.shippingAddress,

			isRep: this.isRep,
			isAdmin: this.isAdmin,
			isSuperAdmin: this.isSuperAdmin,
			isCoveredEntityUser: this.isCoveredEntityUser,
			isAffiliateUser: this.isAffiliateUser,
			isPartnerUser: this.isPartnerUser,
			isHelpStaff: this.isHelpStaff,
			isStaffManager: this.isStaffManager,
			isMarketingManager: this.isMarketingManager,
			isOwner: this.isOwner,
			isDesigner: this.isDesigner,

			notes: this.notes,
			notesCount: this.notesCount,
			insurances: this.insurances,
			insurancesCount: this.insurancesCount,
			documents: this.documents,
			documentsCount: this.documentsCount,
			agreements: this.agreements,
			agreementsCount: this.agreementsCount,
			forms: this.forms,
			formsCount: this.formsCount,

			phoneVerified: this.phoneVerified,

			activityCount: this.activityCount,
			smsCount: this.smsCount,
			emailCount: this.emailCount,
			faceCount: this.faceCount,
			callCount: this.callCount,

			imageRefId: this.imageRefId,

			medications: this.medications,
			medicationsHistory: this.medicationsHistory,

			allergies: this.allergies,
			allergiesHistory: this.allergiesHistory,

			medicalConditions: this.medicalConditions,
			medicalConditionsHistory: this.medicalConditionsHistory,

			isFriendsAndFamily: this.isFriendsAndFamily,
			couponUses: this.couponUses,
			referralCode: this.referralCode,
			preferredContactMethod: this.preferredContactMethod,
			callConsent: this.callConsent,
			communicationPreferences: this.communicationPreferences,
			supportCategories: this.supportCategories,
			supportRoutingStatus: this.supportRoutingStatus,
			supportMaxOpenTickets: this.supportMaxOpenTickets,
			supportRoutingPaused: this.supportRoutingPaused,

			repId: this.repId,

			taskCount: this.taskCount,

			// Customer Analytics & Metadata
			totalSubscriptions: this.totalSubscriptions,
			activeSubscriptions: this.activeSubscriptions,
			cancelledSubscriptions: this.cancelledSubscriptions,
			pendingSubscriptions: this.pendingSubscriptions,
			onHoldSubscriptions: this.onHoldSubscriptions,
			activePrograms: this.activePrograms,
			cancelledPrograms: this.cancelledPrograms,
			pendingPrograms: this.pendingPrograms,
			onHoldPrograms: this.onHoldPrograms,
			activeProducts: this.activeProducts,
			cancelledProducts: this.cancelledProducts,
			pendingProducts: this.pendingProducts,
			onHoldProducts: this.onHoldProducts,
			totalLifetimeValue: this.totalLifetimeValue,
			averageOrderValue: this.averageOrderValue,
			totalOrderCount: this.totalOrderCount,
			customerSince: this.customerSince,
			customerSinceDays: this.customerSinceDays,
			daysSinceFirstOrder: this.daysSinceFirstOrder,
			daysSinceLastOrder: this.daysSinceLastOrder,
			lastOrderDate: this.lastOrderDate,
			firstOrderDate: this.firstOrderDate,
			churnRisk: this.churnRisk,
			customerSegment: this.customerSegment,
			lastEngagementDate: this.lastEngagementDate,
			engagementScore: this.engagementScore,

			hasPastDuePayments: this.hasPastDuePayments,
			hasMissedEncounters: this.hasMissedEncounters,
			hasDelayedShipments: this.hasDelayedShipments,
			needsCall: this.needsCall,
			needsCallCount: this.needsCallCount,
			nextCallActionAt: this.nextCallActionAt,
			newDashboardTutorialStatus: this.newDashboardTutorialStatus,

			metadata: this.metadata
		};
	}

	isValid = () => {
		if (isStringEmpty(this.userId)) {
			throw new Error('userId is required.');
		}

		return true;
	}

	// dates each marketing channel still on as an account created in the browser is made - by staff in admin, or by
	// the support guest form - from the source that made it; an account made on the server is dated there
	dateMarketingConsent = (source, at = moment().utc().toDate()) => {
		const preferences = this.communicationPreferences;
		if (isBooleanTrue(preferences.marketingOptOut)) {
			return this;
		}

		const consent = {};
		if (isBooleanTrue(preferences.marketingEmails) && isNullOrUndefined(preferences.marketingEmailConsentAt)) {
			consent.marketingEmailConsentAt = at;
			consent.marketingEmailConsentSource = source;
		}
		if (isBooleanTrue(preferences.marketingSms) && isNullOrUndefined(preferences.marketingSmsConsentAt)) {
			consent.marketingSmsConsentAt = at;
			consent.marketingSmsConsentSource = source;
		}
		this.communicationPreferences = { ...preferences, ...consent };

		return this;
	}

	// the customer's own device knows where they are; without it, the state the account ships to and then the one it
	// bills to, and failing both the system's own zone. an account made on a staff member's device passes none, since
	// the device is theirs
	resolveTimeZone = (deviceTimeZone = null) => {
		if (isStringEmpty(this.timeZone)) {
			this.timeZone = toAccountTimeZone(deviceTimeZone) ?? getTimeZoneForAddresses(this.shippingAddress, this.billingAddress) ?? DEFAULT_ACCOUNT_TIME_ZONE;
		}

		return this;
	}

	uploadImage = async () => {
		if (isNotNullOrUndefined(this.#image)) {
			const fileName = uuid() + '.' + getFileExt(this.#image.name);
			const uploadRes = await addUserFile(this.#image, 'profile_pictures', fileName, 'PROFILE_PICTURE', 'PENDING', this.userId, []);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				this.imageRefId = uploadRes.ref.fullPath;
			}
		}
	}

	// a new document's claims, for whichever roles it carries; answers whether the login now matches the document. one
	// with no role, or with no login, has nothing to match
	#createRoleClaimsForNewUser = async () => {
		const isAnyRole = ROLE_FLAGS.some((role) => isBooleanTrue(this[role]));
		if (isBooleanFalse(isAnyRole)) {
			return true;
		}

		try {
			return isBooleanTrue(await AuthUser.createRoleClaims(this.userId, toRoleFlags(this), this.phoneVerified));
		} catch (error) {
			if (error?.code === NO_LOGIN_ERROR_CODE) {
				return true;
			}

			throw error;
		}
	}

	// the new document, with isRoleClaimsApplied telling the caller whether its login's claims were written to match, so
	// a profile saved with roles its login lacks is reported rather than shown as a plain success
	create = async () => {
		if (this.isValid()) {
			this.uploadImage();

			const newUser = await createBlindWithId(User.collectionName, this.userId, this.toDto());
			if (isObjectNotEmpty(newUser)) {
				return { ...newUser, isRoleClaimsApplied: await this.#createRoleClaimsForNewUser() };
			}

			return newUser;
		}

		return null;
	}

	// a create that never writes over an existing document: the read and the write are one transaction, so a profile
	// made after any earlier check still stands. answers null when the document already exists
	createIfAbsent = async () => {
		if (this.isValid()) {
			this.uploadImage();

			const ref = doc(db, User.collectionName, this.userId);
			const newUser = await runTransaction(db, async (transaction) => {
				const snapshot = await transaction.get(ref);
				if (snapshot.exists()) {
					return null;
				}

				const data = { ...this.toDto(), ...getCreateFields() };
				transaction.set(ref, data);
				return { ...data, id: this.userId };
			});
			if (isObjectNotEmpty(newUser)) {
				return { ...newUser, isRoleClaimsApplied: await this.#createRoleClaimsForNewUser() };
			}

			return newUser;
		}

		return null;
	}

	static updateImage = async (id, file) => {
		if (isNotNullOrUndefined(file)) {
			const fileName = uuid() + '.' + getFileExt(file.name);
			const uploadRes = await addUserFile(file, 'profile_pictures', fileName, 'PROFILE_PICTURE', 'PENDING', id, []);
			// console.log(uploadRes);

			const photoURL = await getFileUrl(uploadRes.ref.fullPath);
			await AuthUser.update(id, undefined, undefined, undefined, undefined, photoURL);

			if (isObjectNotEmpty(uploadRes)) {
				const success = await this.updateField(id, 'imageRefId', uploadRes.ref.fullPath);
				if (isBooleanTrue(success)) {
					return uploadRes.ref.fullPath;
				}
			}
		}

		return null;
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(User.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	/*
	 * The profile's role flags and the login's claims are one fact staff see in one place, so they change together. The
	 * claims are written first and the profile only once they took, so a failed claims write saves nothing. A profile
	 * write that then fails would leave the login holding roles its profile does not show, so the claims are put back to
	 * the stored profile. Either way staff are told, rather than shown a save that did not happen.
	 */
	static #writeWithRoleClaims = async (id, roles, writeProfile) => {
		const storedUser = await User.getById(id);

		let isMinted = false;
		try {
			isMinted = await AuthUser.createRoleClaims(id, toRoleFlags(roles), undefined);
		} catch (error) {
			// a profile with no login has no claims to keep in step, so it is saved on its own
			if (error?.code === NO_LOGIN_ERROR_CODE) {
				return await writeProfile();
			}

			throw error;
		}

		// a missing answer is a failed write too
		if (!isBooleanTrue(isMinted)) {
			throw new Error('The account\'s permissions could not be updated, so nothing was saved. Please try again.');
		}

		if (isBooleanTrue(await writeProfile())) {
			return true;
		}

		const isRestored = isObjectNotEmpty(storedUser) && isBooleanTrue(await AuthUser.createRoleClaims(id, toRoleFlags(storedUser), undefined));
		throw new Error(isRestored
			? 'The account could not be saved, so its permissions were put back as they were. Please try again.'
			: 'The account could not be saved, and its permissions could not be put back to match it. Open the account and save it again.');
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			// TWO hazards here for anyone writing a caller. `&&` binds tighter than `||`, so this reads as
			// `isRep || isAdmin || isSuperAdmin || (the other eight together)` - naming isRep alone is
			// enough to fire it. And `createRoleClaims` sends every role in ROLE_FLAGS explicitly - it
			// builds the whole set as `false` before applying what it was given - so a flag the caller does
			// not name is sent as an explicit `false` and revoked. (The backend MERGES the payload onto the
			// existing claims, so nothing outside ROLE_FLAGS is at risk.)
			// A caller that names any role flag must name EVERY ONE in ROLE_FLAGS.
			const isRoleChange = updatedFields.isRep !== undefined || updatedFields.isAdmin !== undefined || updatedFields.isSuperAdmin !== undefined || updatedFields.isCoveredEntityUser !== undefined && updatedFields.isAffiliateUser !== undefined && updatedFields.isPartnerUser !== undefined && updatedFields.isHelpStaff !== undefined && updatedFields.isStaffManager !== undefined && updatedFields.isMarketingManager !== undefined && updatedFields.isOwner !== undefined && updatedFields.isDesigner !== undefined;

			if (updatedFields['firstName'] !== undefined) {
				updatedFields['firstNameLower'] = toLower(updatedFields['firstName']);
			}
			if (updatedFields['lastName'] !== undefined) {
				updatedFields['lastNameLower'] = toLower(updatedFields['lastName']);
			}

			const writeProfile = () => updateFields(User.collectionName, id, updatedFields);
			return isRoleChange ? await User.#writeWithRoleClaims(id, updatedFields, writeProfile) : await writeProfile();
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedUser) => {
		const updatedObj = new User(updatedUser);
		if (updatedObj.isValid() && id === updatedUser.id) {
			const isAnyRole = ROLE_FLAGS.some((role) => isBooleanTrue(updatedObj[role]));
			const writeProfile = () => update(User.collectionName, id, updatedObj.toDto());
			const success = isAnyRole ? await User.#writeWithRoleClaims(id, updatedObj, writeProfile) : await writeProfile();
			if (isBooleanTrue(success)) {
				return await User.getById(id);
			}
		} else {
			throw new Error('id and id field of the updated user must match.')
		}

		return null;
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(User.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(User.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getUserProfileById = async (userId) => {
		const userProfile = await this.getById(userId);
		let coveredEntityUsers = [];
		let affiliateUsers = [];
		let partnerUsers = [];
		if (isObjectNotEmpty(userProfile)) {
			// console.log(userProfile);
			if (isBooleanTrue(userProfile.isCoveredEntityUser)) {
				coveredEntityUsers = await CoveredEntityUser.getByUserId(userId);
			}
			if (isBooleanTrue(userProfile.isAffiliateUser)) {
				affiliateUsers = await AffiliateUser.getByUserId(userId);
			}
			if (isBooleanTrue(userProfile.isPartnerUser)) {
				partnerUsers = await PartnerUser.getByUserId(userId);
			}
		}

		return { profile: userProfile, coveredEntityUsers: coveredEntityUsers, affiliateUsers: affiliateUsers, partnerUsers: partnerUsers };
	}

	static getByIds = async (ids) => {
		return await getByIdQuery(User.collectionName, ids, 'in');
	}

	static getAll = async () => {
		return await getAll(User.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer = '==') => {
		return await getBySingleQuery(User.collectionName, fieldName, fieldValue, comparer);
	}

	static getByQueryConstraints = async (queryConstraints = []) => {
		let q = query(collection(db, User.collectionName), ...queryConstraints);

		const snapShot = await getDocs(q);
		// console.log(snapShot)
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static getByPhone = async (phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByEmail = async (email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByFirstNameAndLastNameAndEmailAndPhoneAndPatientId = async (firstName, lastName, email, phone, patientId, userId = null) => {
		let queryConstraints = [];
		const includeOriginalPii = !String(process.env.REACT_APP_ENVIRONMENT).toUpperCase().includes('PROD');

		if (isStringNotEmptyOrZeroLength(firstName) && !isStringNotEmptyOrZeroLength(lastName)) {
			const cleanFirstName = toLower(firstName.trim());
			if (includeOriginalPii) {
				queryConstraints.push(
					or(
						and(
							where('firstNameLower', '>=', cleanFirstName),
							where('firstNameLower', '<=', cleanFirstName + '\uf8ff')
						),
						and(
						  where('originalPii.firstNameLower', '>=', cleanFirstName),
						  where('originalPii.firstNameLower', '<=', cleanFirstName + '\uf8ff')
						),
					)
				);
			} else {
				queryConstraints.push(
					where('firstNameLower', '>=', cleanFirstName),
					where('firstNameLower', '<=', cleanFirstName + '\uf8ff')
				);
			}
		} else if (!isStringNotEmptyOrZeroLength(firstName) && isStringNotEmptyOrZeroLength(lastName)) {
			const cleanLastName = toLower(lastName.trim());
			if (includeOriginalPii) {
				queryConstraints.push(
					or(
						and(
							where('lastNameLower', '>=', cleanLastName),
							where('lastNameLower', '<=', cleanLastName + '\uf8ff')
						),
						and(
						  where('originalPii.lastNameLower', '>=', cleanLastName),
						  where('originalPii.lastNameLower', '<=', cleanLastName + '\uf8ff')
						),
					)
				);
			} else {
				queryConstraints.push(
					where('lastNameLower', '>=', cleanLastName),
					where('lastNameLower', '<=', cleanLastName + '\uf8ff')
				);
			}
		} else if (isStringNotEmptyOrZeroLength(firstName) && isStringNotEmptyOrZeroLength(lastName)) {
			const cleanFirstName = toLower(firstName.trim());
			const cleanLastName = toLower(lastName.trim());
			if (includeOriginalPii) {
				queryConstraints.push(
					and(
						or(
							and(
								where('firstNameLower', '>=', cleanFirstName),
								where('firstNameLower', '<=', cleanFirstName + '\uf8ff')
							),
							and(
								where('originalPii.firstNameLower', '>=', cleanFirstName),
								where('originalPii.firstNameLower', '<=', cleanFirstName + '\uf8ff')
							),
						),
						or(
							and(
								where('lastNameLower', '>=', cleanLastName),
								where('lastNameLower', '<=', cleanLastName + '\uf8ff')
							),
							and(
								where('originalPii.lastNameLower', '>=', cleanLastName),
								where('originalPii.lastNameLower', '<=', cleanLastName + '\uf8ff')
							),
						)
					)
				);
			} else {
				queryConstraints.push(
					where('firstNameLower', '>=', cleanFirstName),
					where('firstNameLower', '<=', cleanFirstName + '\uf8ff'),
					where('lastNameLower', '>=', cleanLastName),
					where('lastNameLower', '<=', cleanLastName + '\uf8ff')
				);
			}
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(
				or(
				  where('email', '==', toLower(email.trim())),
				  where('email', '==', email.trim()), // base64-encoded masking on UAT is case-sensitive
					where('originalPii.email', '==', toLower(email.trim()))
				)
			);
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone.trim());
			const formattedPhone = toE164PhoneNumber(phone.trim());
			queryConstraints.push(where('phone', 'in', [phone.trim(), formattedPhone, cleanPhone]));
		}
		if (isStringNotEmptyOrZeroLength(patientId)) {
			queryConstraints.push(where('patientId', '==', patientId.trim()));
		}
		if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId.trim()));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByNameOrPhoneOrEmail = async (firstName, lastName, phone, email) => {
		const emailMatches = await this.getByFirstNameAndLastNameAndEmailAndPhoneAndPatientId(null, null, email, null, null);
		const phoneMatches = await this.getByFirstNameAndLastNameAndEmailAndPhoneAndPatientId(null, null, null, phone, null);
		const nameMatches = await this.getByFirstNameAndLastNameAndEmailAndPhoneAndPatientId(firstName, lastName, null, null, null);
		const allMatches = [...emailMatches, ...phoneMatches, ...nameMatches];

		// returns unique list
		return [...new Map(allMatches.map((item) => [item.id, item])).values()];
	}

	static getReps = async (searchParameters) => {
		const { state } = searchParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		queryConstraints.push(or(where('isRep', '==', true), where('isStaffManager', '==', true), where('isAdmin', '==', true), where('isSuperAdmin', '==', true)));

		let q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('__name__', 'asc'));

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static getHelpStaff = async (searchParameters) => {
		const { state } = searchParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		queryConstraints.push(or(where('isHelpStaff', '==', true), where('isAdmin', '==', true), where('isSuperAdmin', '==', true)));

		let q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('__name__', 'asc'));

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static getAdmins = async (searchParameters) => {
		const { state } = searchParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		queryConstraints.push(or(where('isAdmin', '==', true), where('isSuperAdmin', '==', true)));

		let q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('__name__', 'asc'));

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static getSuperAdmins = async (searchParameters) => {
		const { state } = searchParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		queryConstraints.push(where('isSuperAdmin', '==', true));

		let q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('__name__', 'asc'));

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static getCount = async (queryParameters) => {
		const { gender, state, excludeState, isRep, isHelpStaff, isAdmin, isSuperAdmin, isFriendsAndFamily, isStaffManager, isMarketingManager, isOwner, repId, churnRisk, customerSegment, minLifetimeValue, maxLifetimeValue, minActiveSubscriptions, maxActiveSubscriptions, activePrograms, activeProducts, hasPastDuePayments, hasMissedEncounters, hasDelayedShipments, minTotalSubscriptions, maxTotalSubscriptions, minTotalOrderCount, maxTotalOrderCount } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(excludeState)) {
			queryConstraints.push(where('shippingAddress.state', 'not-in', excludeState));
		} else if (isStringNotEmptyOrZeroLength(excludeState)) {
			queryConstraints.push(where('shippingAddress.state', '!=', excludeState));
		}

		if (isListNotEmpty(gender)) {
			queryConstraints.push(where('gender', 'in', gender));
		} else if (isStringNotEmptyOrZeroLength(gender)) {
			queryConstraints.push(where('gender', '==', gender));
		}

		if (isBooleanTrue(isRep)) {
			queryConstraints.push(where('isRep', '==', true));
		} else if (isBooleanFalse(isRep)) {
			queryConstraints.push(where('isRep', '==', false));
		}

		if (isBooleanTrue(isHelpStaff)) {
			queryConstraints.push(where('isHelpStaff', '==', true));
		} else if (isBooleanFalse(isHelpStaff)) {
			queryConstraints.push(where('isHelpStaff', '==', false));
		}

		if (isBooleanTrue(isStaffManager)) {
			queryConstraints.push(where('isStaffManager', '==', true));
		} else if (isBooleanFalse(isStaffManager)) {
			queryConstraints.push(where('isStaffManager', '==', false));
		}

		if (isBooleanTrue(isMarketingManager)) {
			queryConstraints.push(where('isMarketingManager', '==', true));
		} else if (isBooleanFalse(isMarketingManager)) {
			queryConstraints.push(where('isMarketingManager', '==', false));
		}

		if (isBooleanTrue(isOwner)) {
			queryConstraints.push(where('isOwner', '==', true));
		} else if (isBooleanFalse(isOwner)) {
			queryConstraints.push(where('isOwner', '==', false));
		}

		if (isBooleanTrue(isAdmin)) {
			queryConstraints.push(where('isAdmin', '==', true));
		} else if (isBooleanFalse(isAdmin)) {
			queryConstraints.push(where('isAdmin', '==', false));
		}

		if (isBooleanTrue(isSuperAdmin)) {
			queryConstraints.push(where('isSuperAdmin', '==', true));
		} else if (isBooleanFalse(isSuperAdmin)) {
			queryConstraints.push(where('isSuperAdmin', '==', false));
		}

		if (isBooleanTrue(isFriendsAndFamily)) {
			queryConstraints.push(where('isFriendsAndFamily', '==', true));
		} else if (isBooleanFalse(isFriendsAndFamily)) {
			queryConstraints.push(where('isFriendsAndFamily', '==', false));
		}

		if (isListNotEmpty(repId)) {
			queryConstraints.push(where('repId', 'in', repId));
		} else if (isStringNotEmptyOrZeroLength(repId)) {
			if (repId === 'NONE') {
				queryConstraints.push(where('repId', '==', 'NONE'));
			} else if (repId === 'ASSIGNED') {
				queryConstraints.push(where('repId', '!=', 'NONE'));
			} else {
				queryConstraints.push(where('repId', '==', repId));
			}
		}

		if (isListNotEmpty(churnRisk)) {
			queryConstraints.push(where('churnRisk', 'in', churnRisk));
		} else if (isStringNotEmptyOrZeroLength(churnRisk)) {
			queryConstraints.push(where('churnRisk', '==', churnRisk));
		}

		if (isListNotEmpty(customerSegment)) {
			queryConstraints.push(where('customerSegment', 'in', customerSegment));
		} else if (isStringNotEmptyOrZeroLength(customerSegment)) {
			queryConstraints.push(where('customerSegment', '==', customerSegment));
		}

		if (isNumberGreaterThanOrEqualToZero(minTotalSubscriptions)) {
			queryConstraints.push(where('totalSubscriptions', '>=', minTotalSubscriptions));
		}

		if (isNumberGreaterThanZero(maxTotalSubscriptions)) {
			queryConstraints.push(where('totalSubscriptions', '<=', maxTotalSubscriptions));
		}

		if (isNumberGreaterThanOrEqualToZero(minTotalOrderCount)) {
			queryConstraints.push(where('totalOrderCount', '>=', minTotalOrderCount));
		}

		if (isNumberGreaterThanZero(maxTotalOrderCount)) {
			queryConstraints.push(where('totalOrderCount', '<=', maxTotalOrderCount));
		}

		if (isNumberGreaterThanOrEqualToZero(minLifetimeValue)) {
			queryConstraints.push(where('totalLifetimeValue', '>=', minLifetimeValue));
		}

		if (isNumberGreaterThanZero(maxLifetimeValue)) {
			queryConstraints.push(where('totalLifetimeValue', '<=', maxLifetimeValue));
		}

		if (isNumberGreaterThanOrEqualToZero(minActiveSubscriptions)) {
			queryConstraints.push(where('activeSubscriptions', '>=', minActiveSubscriptions));
		}

		if (isNumberGreaterThanZero(maxActiveSubscriptions)) {
			queryConstraints.push(where('activeSubscriptions', '<=', maxActiveSubscriptions));
		}

		if (isListNotEmpty(activePrograms)) {
			queryConstraints.push(where('activePrograms', 'array-contains-any', activePrograms));
		}

		if (isListNotEmpty(activeProducts)) {
			queryConstraints.push(where('activeProducts', 'array-contains-any', activeProducts));
		}

		if (isBooleanTrue(hasPastDuePayments)) {
			queryConstraints.push(where('hasPastDuePayments', '==', true));
		} else if (isBooleanFalse(hasPastDuePayments)) {
			queryConstraints.push(where('hasPastDuePayments', '==', false));
		}

		if (isBooleanTrue(hasMissedEncounters)) {
			queryConstraints.push(where('hasMissedEncounters', '==', true));
		} else if (isBooleanFalse(hasMissedEncounters)) {
			queryConstraints.push(where('hasMissedEncounters', '==', false));
		}

		if (isBooleanTrue(hasDelayedShipments)) {
			queryConstraints.push(where('hasDelayedShipments', '==', true));
		} else if (isBooleanFalse(hasDelayedShipments)) {
			queryConstraints.push(where('hasDelayedShipments', '==', false));
		}
		console.log('queryConstraints:', queryConstraints);
		let q = query(collection(db, User.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible) => {
		const { gender, state, excludeState, isRep, isHelpStaff, isAdmin, isSuperAdmin, isFriendsAndFamily, isStaffManager, isMarketingManager, isOwner, repId, churnRisk, customerSegment, minLifetimeValue, maxLifetimeValue, minActiveSubscriptions, maxActiveSubscriptions, activePrograms, activeProducts, hasPastDuePayments, hasMissedEncounters, hasDelayedShipments, minTotalSubscriptions, maxTotalSubscriptions, minTotalOrderCount, maxTotalOrderCount } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(excludeState)) {
			queryConstraints.push(where('shippingAddress.state', 'not-in', excludeState));
		} else if (isStringNotEmptyOrZeroLength(excludeState)) {
			queryConstraints.push(where('shippingAddress.state', '!=', excludeState));
		}

		if (isListNotEmpty(gender)) {
			queryConstraints.push(where('gender', 'in', gender));
		} else if (isStringNotEmptyOrZeroLength(gender)) {
			queryConstraints.push(where('gender', '==', gender));
		}

		if (isBooleanTrue(isRep)) {
			queryConstraints.push(where('isRep', '==', true));
		} else if (isBooleanFalse(isRep)) {
			queryConstraints.push(where('isRep', '==', false));
		}

		if (isBooleanTrue(isHelpStaff)) {
			queryConstraints.push(where('isHelpStaff', '==', true));
		} else if (isBooleanFalse(isHelpStaff)) {
			queryConstraints.push(where('isHelpStaff', '==', false));
		}

		if (isBooleanTrue(isStaffManager)) {
			queryConstraints.push(where('isStaffManager', '==', true));
		} else if (isBooleanFalse(isStaffManager)) {
			queryConstraints.push(where('isStaffManager', '==', false));
		}

		if (isBooleanTrue(isMarketingManager)) {
			queryConstraints.push(where('isMarketingManager', '==', true));
		} else if (isBooleanFalse(isMarketingManager)) {
			queryConstraints.push(where('isMarketingManager', '==', false));
		}

		if (isBooleanTrue(isOwner)) {
			queryConstraints.push(where('isOwner', '==', true));
		} else if (isBooleanFalse(isOwner)) {
			queryConstraints.push(where('isOwner', '==', false));
		}

		if (isBooleanTrue(isAdmin)) {
			queryConstraints.push(where('isAdmin', '==', true));
		} else if (isBooleanFalse(isAdmin)) {
			queryConstraints.push(where('isAdmin', '==', false));
		}

		if (isBooleanTrue(isSuperAdmin)) {
			queryConstraints.push(where('isSuperAdmin', '==', true));
		} else if (isBooleanFalse(isSuperAdmin)) {
			queryConstraints.push(where('isSuperAdmin', '==', false));
		}

		if (isBooleanTrue(isFriendsAndFamily)) {
			queryConstraints.push(where('isFriendsAndFamily', '==', true));
		} else if (isBooleanFalse(isFriendsAndFamily)) {
			queryConstraints.push(where('isFriendsAndFamily', '==', false));
		}

		if (isListNotEmpty(repId)) {
			queryConstraints.push(where('repId', 'in', repId));
		} else if (isStringNotEmptyOrZeroLength(repId)) {
			if (repId === 'NONE') {
				queryConstraints.push(where('repId', '==', 'NONE'));
			} else if (repId === 'ASSIGNED') {
				queryConstraints.push(where('repId', '!=', 'NONE'));
			} else {
				queryConstraints.push(where('repId', '==', repId));
			}
		}

		if (isListNotEmpty(churnRisk)) {
			queryConstraints.push(where('churnRisk', 'in', churnRisk));
		} else if (isStringNotEmptyOrZeroLength(churnRisk)) {
			queryConstraints.push(where('churnRisk', '==', churnRisk));
		}

		if (isListNotEmpty(customerSegment)) {
			queryConstraints.push(where('customerSegment', 'in', customerSegment));
		} else if (isStringNotEmptyOrZeroLength(customerSegment)) {
			queryConstraints.push(where('customerSegment', '==', customerSegment));
		}

		if (isNumberGreaterThanOrEqualToZero(minTotalSubscriptions)) {
			queryConstraints.push(where('totalSubscriptions', '>=', minTotalSubscriptions));
		}

		if (isNumberGreaterThanZero(maxTotalSubscriptions)) {
			queryConstraints.push(where('totalSubscriptions', '<=', maxTotalSubscriptions));
		}

		if (isNumberGreaterThanOrEqualToZero(minTotalOrderCount)) {
			queryConstraints.push(where('totalOrderCount', '>=', minTotalOrderCount));
		}

		if (isNumberGreaterThanZero(maxTotalOrderCount)) {
			queryConstraints.push(where('totalOrderCount', '<=', maxTotalOrderCount));
		}

		if (isNumberGreaterThanOrEqualToZero(minLifetimeValue)) {
			queryConstraints.push(where('totalLifetimeValue', '>=', minLifetimeValue));
		}

		if (isNumberGreaterThanZero(maxLifetimeValue)) {
			queryConstraints.push(where('totalLifetimeValue', '<=', maxLifetimeValue));
		}

		if (isNumberGreaterThanOrEqualToZero(minActiveSubscriptions)) {
			queryConstraints.push(where('activeSubscriptions', '>=', minActiveSubscriptions));
		}

		if (isNumberGreaterThanZero(maxActiveSubscriptions)) {
			queryConstraints.push(where('activeSubscriptions', '<=', maxActiveSubscriptions));
		}

		if (isListNotEmpty(activePrograms)) {
			queryConstraints.push(where('activePrograms', 'array-contains-any', activePrograms));
		}

		if (isListNotEmpty(activeProducts)) {
			queryConstraints.push(where('activeProducts', 'array-contains-any', activeProducts));
		}

		if (isBooleanTrue(hasPastDuePayments)) {
			queryConstraints.push(where('hasPastDuePayments', '==', true));
		} else if (isBooleanFalse(hasPastDuePayments)) {
			queryConstraints.push(where('hasPastDuePayments', '==', false));
		}

		if (isBooleanTrue(hasMissedEncounters)) {
			queryConstraints.push(where('hasMissedEncounters', '==', true));
		} else if (isBooleanFalse(hasMissedEncounters)) {
			queryConstraints.push(where('hasMissedEncounters', '==', false));
		}

		if (isBooleanTrue(hasDelayedShipments)) {
			queryConstraints.push(where('hasDelayedShipments', '==', true));
		} else if (isBooleanFalse(hasDelayedShipments)) {
			queryConstraints.push(where('hasDelayedShipments', '==', false));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			if (isNotNullOrUndefined(minTotalSubscriptions) || isNotNullOrUndefined(maxTotalSubscriptions)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalSubscriptions', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible['totalSubscriptions'], firstVisible['id']));
			} else if (isNotNullOrUndefined(minLifetimeValue) || isNotNullOrUndefined(maxLifetimeValue)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalLifetimeValue', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible['totalLifetimeValue'], firstVisible['id']));
			} else if (isNotNullOrUndefined(minTotalOrderCount) || isNotNullOrUndefined(maxTotalOrderCount)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalOrderCount', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible['totalOrderCount'], firstVisible['id']));
			} else if (isNotNullOrUndefined(minActiveSubscriptions) || isNotNullOrUndefined(maxActiveSubscriptions)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('activeSubscriptions', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible['activeSubscriptions'], firstVisible['id']));
			} else {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible['lastName'], firstVisible['firstName'], firstVisible['repId'], firstVisible['id']));
			}
		} else if (isObjectNotEmpty(lastVisible)) {
			if (isNotNullOrUndefined(minTotalSubscriptions) || isNotNullOrUndefined(maxTotalSubscriptions)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalSubscriptions', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible['totalSubscriptions'], lastVisible['id']));
			} else if (isNotNullOrUndefined(minLifetimeValue) || isNotNullOrUndefined(maxLifetimeValue)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalLifetimeValue', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible['totalLifetimeValue'], lastVisible['id']));
			} else if (isNotNullOrUndefined(minTotalOrderCount) || isNotNullOrUndefined(maxTotalOrderCount)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalOrderCount', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible['totalOrderCount'], lastVisible['id']));
			} else if (isNotNullOrUndefined(minActiveSubscriptions) || isNotNullOrUndefined(maxActiveSubscriptions)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('activeSubscriptions', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible['activeSubscriptions'], lastVisible['id']));
			} else {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible['lastName'], lastVisible['firstName'], lastVisible['repId'], lastVisible['id']));
			}
		} else {
			if (isNotNullOrUndefined(minTotalSubscriptions) || isNotNullOrUndefined(maxTotalSubscriptions)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalSubscriptions', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
			} else if (isNotNullOrUndefined(minLifetimeValue) || isNotNullOrUndefined(maxLifetimeValue)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalLifetimeValue', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
			} else if (isNotNullOrUndefined(minTotalOrderCount) || isNotNullOrUndefined(maxTotalOrderCount)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('totalOrderCount', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
			} else if (isNotNullOrUndefined(minActiveSubscriptions) || isNotNullOrUndefined(maxActiveSubscriptions)) {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('activeSubscriptions', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
			} else {
				q = query(collection(db, User.collectionName), ...queryConstraints, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
			}
		}

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next);

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
			throw error;
		}

		return allRecords;
	}

	static updateUserInsurances = async (userId, insurances) => {
		let updatedInsurances = [];
		if (isStringNotEmpty(userId)) {
			try {
				let existingInsurances = await UserInsurance.getByUserId(userId);
				updatedInsurances = [...existingInsurances];
				for (const insurance of insurances) {
					// check to see if insurance exists already for this user based on payercode and update insurance instead of creating a new record
					if (existingInsurances.some(i => i.payerCode === insurance.payerCode && i.subscriberId === insurance.subscriberId)) {
						let existingInsurance = existingInsurances.find(i => i.payerCode === insurance.payerCode && i.subscriberId === insurance.subscriberId);
						if (isObjectNotEmpty(existingInsurance) && isStringNotEmpty(existingInsurance.id)) {
							let success = await UserInsurance.updateFields(userId, existingInsurance.id, {
								subscriberId: insurance.subscriberId,
								rank: insurance.rank,
								hierarchy: insurance.hierarchy,
								status: insurance.status,
								isMedicare: insurance.isMedicare,
								isMedicaid: insurance.isMedicaid,

								firstName: insurance.firstName,
								middleName: insurance.middleName,
								lastName: insurance.lastName,
								suffix: insurance.suffix,

								gender: insurance.gender,
								dateOfBirth: insurance.dateOfBirth,

								socialSecurityNumber: insurance.socialSecurityNumber,

								patientPhone: insurance.patientPhone,
								patientAddress: insurance.patientAddress,
								relationshipToSubscriber: insurance.relationshipToSubscriber,

								insuranceAddress: insurance.insuranceAddress,
								insurancePhone: insurance.insurancePhone,

								subscriberFirstName: insurance.subscriberFirstName,
								subscriberLastName: insurance.subscriberLastName,
								subscriberPhone: insurance.subscriberPhone,
								subscriberAddress: insurance.subscriberAddress,

								payerName: insurance.payerName,
								payerCode: insurance.payerCode,
								planName: insurance.planName,
								planNumber: insurance.planNumber,
								groupName: insurance.groupName,
								groupNumber: insurance.groupNumber,
								planType: insurance.planType,
								policyType: insurance.policyType,

								rxBin: insurance.rxBin,
								rxPcn: insurance.rxPcn,
								rxGroup: insurance.rxGroup,

								effectiveFromDate: insurance.effectiveFromDate,
								effectiveToDate: insurance.effectiveToDate,

								insuranceInfo: insurance.insuranceInfo
							});
							if (isBooleanTrue(success)) {
								updatedInsurances = addInsuranceRecordToBottom({
									...existingInsurance,

									subscriberId: insurance.subscriberId,
									rank: insurance.rank,
									hierarchy: insurance.hierarchy,
									status: insurance.status,
									isMedicare: insurance.isMedicare,
									isMedicaid: insurance.isMedicaid,

									firstName: insurance.firstName,
									middleName: insurance.middleName,
									lastName: insurance.lastName,
									suffix: insurance.suffix,

									gender: insurance.gender,
									dateOfBirth: insurance.dateOfBirth,

									socialSecurityNumber: insurance.socialSecurityNumber,

									patientPhone: insurance.patientPhone,
									patientAddress: insurance.patientAddress,
									relationshipToSubscriber: insurance.relationshipToSubscriber,

									insuranceAddress: insurance.insuranceAddress,
									insurancePhone: insurance.insurancePhone,

									subscriberFirstName: insurance.subscriberFirstName,
									subscriberLastName: insurance.subscriberLastName,
									subscriberPhone: insurance.subscriberPhone,
									subscriberAddress: insurance.subscriberAddress,

									payerName: insurance.payerName,
									payerCode: insurance.payerCode,
									planName: insurance.planName,
									planNumber: insurance.planNumber,
									groupName: insurance.groupName,
									groupNumber: insurance.groupNumber,
									planType: insurance.planType,
									policyType: insurance.policyType,

									rxBin: insurance.rxBin,
									rxPcn: insurance.rxPcn,
									rxGroup: insurance.rxGroup,

									effectiveFromDate: insurance.effectiveFromDate,
									effectiveToDate: insurance.effectiveToDate,

									insuranceInfo: insurance.insuranceInfo
								}, [...updatedInsurances]);
							}
						}
					} else {
						let newInsurance = await new UserInsurance({
							userId: userId,
							subscriberId: insurance.subscriberId,
							rank: insurance.rank,
							hierarchy: insurance.hierarchy,
							status: insurance.status,
							isMedicare: insurance.isMedicare,
							isMedicaid: insurance.isMedicaid,

							firstName: insurance.firstName,
							middleName: insurance.middleName,
							lastName: insurance.lastName,
							suffix: insurance.suffix,

							gender: insurance.gender,
							dateOfBirth: insurance.dateOfBirth,

							socialSecurityNumber: insurance.socialSecurityNumber,

							patientPhone: insurance.patientPhone,
							patientAddress: insurance.patientAddress,
							relationshipToSubscriber: insurance.relationshipToSubscriber,

							insurancePhone: null,
							insuranceAddress: insurance.insuranceAddress,

							subscriberFirstName: insurance.subscriberFirstName,
							subscriberLastName: insurance.subscriberLastName,
							subscriberPhone: insurance.subscriberPhone,
							subscriberAddress: insurance.subscriberAddress,

							payerName: insurance.payerName,
							payerCode: insurance.payerCode,
							planName: insurance.planName,
							planNumber: insurance.planNumber,
							groupName: insurance.groupName,
							groupNumber: insurance.groupNumber,
							planType: insurance.planType,
							policyType: insurance.policyType,

							rxBin: insurance.rxBin,
							rxPcn: insurance.rxPcn,
							rxGroup: insurance.rxGroup,

							effectiveFromDate: insurance.effectiveFromDate,
							effectiveToDate: insurance.effectiveToDate,

							insuranceInfo: insurance.insuranceInfo
						}).create();

						updatedInsurances = addInsuranceRecordToBottom(newInsurance, [...updatedInsurances]);
					}
				}

				return { success: true, updatedInsurances: updatedInsurances };
			} catch (error) {
				console.log(error);
			}
		}

		return { success: false, updatedInsurances: updatedInsurances };
	}

	static updateUsers = async () => {
		const list = await this.get({});

		let chunks = [];
		for (let i = 0; i < list.length; i += 500) {
			chunks.push(list.slice(i, i + 500))
		}
		console.log(chunks.length);
		for (var chunk of chunks) {
			// Get a new write batch
			var batch = writeBatch(db);
			chunk.map((item) => {
				const userRef = doc(collection(db, User.collectionName), item.id);

				let updatedFields = {};
				if (isBooleanFalse(item.phoneVerified)) { updatedFields.phoneVerified = false; }
				if (!isNumberGreaterThanZero(item.activityCount)) { updatedFields.activityCount = 0; }
				if (!isNumberGreaterThanZero(item.smsCount)) { updatedFields.smsCount = 0; }
				if (!isNumberGreaterThanZero(item.emailCount)) { updatedFields.emailCount = 0; }
				if (!isNumberGreaterThanZero(item.faceCount)) { updatedFields.faceCount = 0; }
				if (!isNumberGreaterThanZero(item.callCount)) { updatedFields.callCount = 0; }

				if (isObjectNotEmpty(updatedFields)) {
					batch.update(userRef, updatedFields);
				}
			});
			await batch.commit();
			console.log('done');
		}
	}

	static updateRep = async (userId, repId) => {
		// a DENIED subscription moves too, since customer service may restore it
		let subscriptions = await Subscription.getByUserIdAndStatus(userId, ['PENDING', 'ACTIVE', 'ON_HOLD', 'DENIED']);
		for (const subscription of subscriptions) {
			await Subscription.updateField(subscription.id, 'repId', repId);
		}

		let orders = await Order.getByUserIdAndStatus(userId, ['DRAFT', 'CONFIRMED', 'APPROVED', 'READY', 'PENDING']);
		for (const order of orders) {
			await Order.updateField(order.id, 'repId', repId);
		}

		return await updateField(User.collectionName, userId, 'repId', repId);
	}

	static assignRepToUsers = async (repId, patientCount, state) => {
		let users = await this.getPaginated({
			state: state,
			repId: "NONE"
		}, patientCount, null, null);

		for (const user of users) {
			await this.updateRep(user.id, repId);
		}

		// let chunks = [];
		// for (let i = 0; i < users.length; i += 500) {
		// 	chunks.push(users.slice(i, i + 500))
		// }
		// // console.log(chunks.length);
		// for (var chunk of chunks) {
		// 	// Get a new write batch
		// 	var batch = writeBatch(db);
		// 	chunk.map((item) => {
		// 		const userRef = doc(collection(db, User.collectionName), item.userId);
		// 		batch.update(userRef, { repId: repId });
		// 	});
		// 	await batch.commit();
		// 	// console.log('done');
		// }

		return true;
	}

	static transferRepToUsers = async (repId, users) => {
		for (const user of users) {
			await this.updateRep(user.id, repId);
		}

		// let chunks = [];
		// for (let i = 0; i < users.length; i += 500) {
		// 	chunks.push(users.slice(i, i + 500))
		// }
		// // console.log(chunks.length);
		// for (var chunk of chunks) {
		// 	// Get a new write batch
		// 	var batch = writeBatch(db);
		// 	chunk.map((item) => {
		// 		const userRef = doc(collection(db, User.collectionName), item.userId);
		// 		batch.update(userRef, { repId: repId });
		// 	});
		// 	await batch.commit();
		// 	// console.log('done');
		// }

		return true;
	}

	static assignRepToUserIds = async (repId, userIds) => {
		for (const userId of userIds) {
			await this.updateRep(userId, repId);
		}

		return true;
	}

	// Utility methods for updating customer analytics metadata
	static updateCustomerAnalytics = async (userId, analyticsData) => {
		if (isStringNotEmpty(userId) && isObjectNotEmpty(analyticsData)) {
			const updatedFields = {};

			if (isNumberGreaterThanOrEqualToZero(analyticsData.totalSubscriptions)) { updatedFields.totalSubscriptions = analyticsData.totalSubscriptions; }
			if (isNumberGreaterThanOrEqualToZero(analyticsData.activeSubscriptions)) { updatedFields.activeSubscriptions = analyticsData.activeSubscriptions; }
			if (isNumberGreaterThanOrEqualToZero(analyticsData.cancelledSubscriptions)) { updatedFields.cancelledSubscriptions = analyticsData.cancelledSubscriptions; }
			if (isListNotEmpty(analyticsData.activePrograms)) { updatedFields.activePrograms = analyticsData.activePrograms; }
			if (isListNotEmpty(analyticsData.cancelledPrograms)) { updatedFields.cancelledPrograms = analyticsData.cancelledPrograms; }
			if (isListNotEmpty(analyticsData.activeProducts)) { updatedFields.activeProducts = analyticsData.activeProducts; }
			if (isListNotEmpty(analyticsData.cancelledProducts)) { updatedFields.cancelledProducts = analyticsData.cancelledProducts; }
			if (isNumberGreaterThanOrEqualToZero(analyticsData.totalLifetimeValue)) { updatedFields.totalLifetimeValue = analyticsData.totalLifetimeValue; }
			if (isNumberGreaterThanOrEqualToZero(analyticsData.averageOrderValue)) { updatedFields.averageOrderValue = analyticsData.averageOrderValue; }
			if (isNumberGreaterThanOrEqualToZero(analyticsData.totalOrderCount)) { updatedFields.totalOrderCount = analyticsData.totalOrderCount; }
			if (isNotNullOrUndefined(analyticsData.customerSince)) { updatedFields.customerSince = analyticsData.customerSince; }
			// Remove daysSinceFirstOrder and daysSinceLastOrder from direct updates
			// These should be calculated dynamically
			if (isNotNullOrUndefined(analyticsData.lastOrderDate)) {
				updatedFields.lastOrderDate = analyticsData.lastOrderDate;
			}
			if (isNotNullOrUndefined(analyticsData.firstOrderDate)) {
				updatedFields.firstOrderDate = analyticsData.firstOrderDate;
			}
			if (isStringNotEmpty(analyticsData.churnRisk)) { updatedFields.churnRisk = analyticsData.churnRisk; }
			if (isStringNotEmpty(analyticsData.customerSegment)) { updatedFields.customerSegment = analyticsData.customerSegment; }
			if (isNotNullOrUndefined(analyticsData.lastEngagementDate)) {
				updatedFields.lastEngagementDate = analyticsData.lastEngagementDate;
			}
			if (isNumberGreaterThanOrEqualToZero(analyticsData.engagementScore)) {
				updatedFields.engagementScore = analyticsData.engagementScore;
			}
			if (analyticsData.hasPastDuePayments !== undefined) { updatedFields.hasPastDuePayments = analyticsData.hasPastDuePayments; }
			if (analyticsData.hasMissedEncounters !== undefined) { updatedFields.hasMissedEncounters = analyticsData.hasMissedEncounters; }
			if (analyticsData.hasDelayedShipments !== undefined) { updatedFields.hasDelayedShipments = analyticsData.hasDelayedShipments; }

			if (isObjectNotEmpty(updatedFields)) {
				return await this.updateFields(userId, updatedFields);
			}
		}
		return false;
	}

	// Calculate dynamic date-based fields
	static calculateDaysSinceDate = (dateValue) => {
		if (!isNotNullOrUndefined(dateValue)) return null; // Return null instead of 0

		// Handle both Firestore Timestamp and Date objects
		let targetDate;
		if (dateValue.toDate && typeof dateValue.toDate === 'function') {
			// Firestore Timestamp
			targetDate = dateValue.toDate();
		} else if (dateValue instanceof Date) {
			// JavaScript Date
			targetDate = dateValue;
		} else {
			// Fallback for string dates
			targetDate = new Date(dateValue);
		}

		const currentDate = new Date();
		const timeDifference = currentDate.getTime() - targetDate.getTime();
		const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));

		return daysDifference >= 0 ? daysDifference : null;
	}

	// Calculate churn risk
	static calculateChurnRisk = (user) => {
		let riskLevel = 'LOW';

		const daysSinceLastOrder = isNotNullOrUndefined(user.lastOrderDate)
			? this.calculateDaysSinceDate(user.lastOrderDate)
			: null;

		const engagementScore = user.engagementScore || 0;
		const activeSubscriptions = user.activeSubscriptions || 0;

		// If no orders ever, high risk
		if (daysSinceLastOrder === null) {
			return 'HIGH';
		}

		// Simplified churn risk calculation without payment cycles
		if (daysSinceLastOrder > 90 || (engagementScore < 10 && activeSubscriptions === 0)) {
			riskLevel = 'HIGH';
		} else if (daysSinceLastOrder > 30 || engagementScore < 25) {
			riskLevel = 'MEDIUM';
		}

		return riskLevel;
	}

	// Batch update calculated fields for users with orders (scheduled job)
	static updateCalculatedFieldsForSubscriptionUsers = async () => {
		// Only get users with totalSubscriptions > 0
		const users = await this.get({
			minActiveSubscriptions: 1
		});

		let chunks = [];
		for (let i = 0; i < users.length; i += 500) {
			chunks.push(users.slice(i, i + 500));
		}

		console.log(`Updating calculated fields for ${users.length} subscription users in ${chunks.length} chunks`);

		for (const chunk of chunks) {
			const batch = writeBatch(db);

			chunk.forEach((user) => {
				const userRef = doc(collection(db, User.collectionName), user.id);
				const updatedFields = {};

				// Calculate and update dynamic fields
				const daysSinceFirstOrder = isNotNullOrUndefined(user.firstOrderDate)
					? this.calculateDaysSinceDate(user.firstOrderDate)
					: null;
				const daysSinceLastOrder = isNotNullOrUndefined(user.lastOrderDate)
					? this.calculateDaysSinceDate(user.lastOrderDate)
					: null;
				const customerSinceDays = isNotNullOrUndefined(user.customerSince)
					? this.calculateDaysSinceDate(user.customerSince)
					: null;

				// Only update if values changed
				if (user.daysSinceFirstOrder !== daysSinceFirstOrder) {
					updatedFields.daysSinceFirstOrder = daysSinceFirstOrder;
				}
				if (user.daysSinceLastOrder !== daysSinceLastOrder) {
					updatedFields.daysSinceLastOrder = daysSinceLastOrder;
				}
				if (user.customerSinceDays !== customerSinceDays) {
					updatedFields.customerSinceDays = customerSinceDays;
				}

				// Recalculate churn risk based on payment cycle
				const churnRisk = this.calculateChurnRisk({
					...user,
					daysSinceLastOrder
				});

				if (churnRisk !== user.churnRisk) {
					updatedFields.churnRisk = churnRisk;
				}

				if (Object.keys(updatedFields).length > 0) {
					batch.update(userRef, updatedFields);
				}
			});

			await batch.commit();
			console.log(`Updated chunk of ${chunk.length} users`);
		}

		console.log('Finished updating calculated fields for subscription users');
		return { success: true, usersProcessed: users.length };
	}

	// Updated method to handle subscription payment cycles
	static updateLifetimeValue = async (userId, orderValue, isFirstOrder = false) => {
		if (isStringNotEmpty(userId) && isNumberGreaterThanZero(orderValue)) {
			const user = await this.getById(userId);
			if (isObjectNotEmpty(user)) {
				const currentLTV = user.totalLifetimeValue || 0;
				const currentOrderCount = user.totalOrderCount || 0;
				const newOrderCount = currentOrderCount + 1;
				const newLTV = currentLTV + orderValue;
				const newAOV = newLTV / newOrderCount;
				const nowDate = new Date();

				let updatedFields = {
					totalLifetimeValue: newLTV,
					averageOrderValue: newAOV,
					totalOrderCount: newOrderCount,
					lastOrderDate: nowDate
				};

				if (isFirstOrder || !isNotNullOrUndefined(user.firstOrderDate)) {
					updatedFields.firstOrderDate = nowDate;
					if (!isNotNullOrUndefined(user.customerSince)) {
						updatedFields.customerSince = nowDate;
					}
				}

				return await this.updateFields(userId, updatedFields);
			}
		}
		return false;
	}

	// Method to update status tracking flags
	static updateStatusFlags = async (userId, statusFlags) => {
		if (isStringNotEmpty(userId) && isObjectNotEmpty(statusFlags)) {
			const updatedFields = {};

			if (statusFlags.hasPastDuePayments !== undefined) {
				updatedFields.hasPastDuePayments = statusFlags.hasPastDuePayments;
			}
			if (statusFlags.hasMissedEncounters !== undefined) {
				updatedFields.hasMissedEncounters = statusFlags.hasMissedEncounters;
			}
			if (statusFlags.hasDelayedShipments !== undefined) {
				updatedFields.hasDelayedShipments = statusFlags.hasDelayedShipments;
			}

			if (Object.keys(updatedFields).length > 0) {
				return await this.updateFields(userId, updatedFields);
			}
		}
		return false;
	}

	static updateEngagementMetrics = async (userId, activityType = 'GENERAL') => {
		if (isStringNotEmpty(userId)) {
			const user = await this.getById(userId);
			if (isObjectNotEmpty(user)) {
				let scoreIncrement = 1;

				// Different activity types have different engagement values
				switch (activityType) {
					case 'ORDER':
						scoreIncrement = 10;
						break;
					case 'SUBSCRIPTION':
						scoreIncrement = 15;
						break;
					case 'LOGIN':
						scoreIncrement = 2;
						break;
					case 'EMAIL_OPEN':
						scoreIncrement = 1;
						break;
					case 'SMS_RESPONSE':
						scoreIncrement = 3;
						break;
					default:
						scoreIncrement = 1;
				}

				const currentScore = user.engagementScore || 0;
				const updatedFields = {
					engagementScore: currentScore + scoreIncrement,
					lastEngagementDate: new Date()
				};

				return await this.updateFields(userId, updatedFields);
			}
		}
		return false;
	}

	// Method to get users with high churn risk (for daily monitoring)
	static getAtRiskUsers = async () => {
		// Get users with high churn risk
		const highChurnUsers = await this.getByQueryConstraints([
			where('churnRisk', '==', 'HIGH'),
			where('totalSubscriptions', '>', 0)
		]);

		// Get users with status issues
		const pastDueUsers = await this.getByQueryConstraints([
			where('hasPastDuePayments', '==', true),
			where('activeSubscriptions', '>', 0)
		]);

		// Combine and deduplicate
		return [...highChurnUsers, ...pastDueUsers].reduce((unique, user) => {
			return unique.some(u => u.id === user.id) ? unique : [...unique, user];
		}, []);
	}

	// Comprehensive analytics backfill method
	static backfillAllAnalytics = async () => {
		console.log('Starting comprehensive analytics backfill for all users...');

		let totalProcessed = 0;
		let totalUpdated = 0;
		let queryParameters = {};

		let programs = await Program.getAll();

		try {
			try {
				let next = null;

				let totalCount = await User.getCount(queryParameters);
				let totalPages = Math.ceil(totalCount / 500.0);
				console.log('totalCount: ' + totalCount);
				console.log('totalPages: ' + totalPages);
				let pages = [];
				for (let i = 0; i <= totalPages; i++) {
					pages.push(i + 1);
				}

				for (const page of pages) {
					console.log('starting page ' + page);

					let unfilteredList = await User.getPaginated(queryParameters, 500, null, next);
					console.log(`Fetched ${unfilteredList.length} users for chunk`);
					let list = unfilteredList.filter(user => user.totalSubscriptions > 0);

					const batch = writeBatch(db);
					let batchUpdates = 0;

					// Get all data we need for this chunk in parallel
					const userIds = list.map(user => user.userId);

					let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

					// Get subscriptions for all users in chunk
					const allSubscriptions = await getBySingleQuery(Subscription.collectionName, 'userId', userIds, 'in');
					console.log(`Fetched ${allSubscriptions.length} subscriptions for chunk`);
					// Get voucher orders for all users in chunk
					const allOrders = await getBySingleQuery(Order.collectionName, 'userId', list.map(user => user.userId), 'in');
					const allVoucherOrders = allOrders.filter(i => i.orderType === 'VOUCHER');
					console.log(`Fetched ${allVoucherOrders.length} voucher orders for chunk`);
					// Get missed encounters for all users in chunk
					const allEncounters = await getBySingleQuery(Encounter.collectionName, 'userId', list.map(user => user.userId), 'in');
					const allMissedEncounters = allEncounters.filter(i => ['SCHEDULED', 'REQUESTED'].includes(i.status) && i.visitType === 'FOLLOWUP' && fromUtcToDate(i.scheduledAt) <= startDate);
					console.log(`Fetched ${allMissedEncounters.length} missed encounters for chunk`);
					// Get delayed prescription orders for all users in chunk
					const allPrescriptions = await getBySingleQuery(Prescription.collectionName, 'userId', list.map(user => user.userId), 'in')
					const allDelayedPrescriptions = allPrescriptions.filter(i => ['SUBMITTED', 'RECEIVED', 'QUEUED', 'PREVERIFICATION', 'FILLING', 'VERIFIED', 'FILLED', 'SHIPPED'].includes(i.status) && fromUtcToDate(i.submittedAt) <= startDate);
					console.log(`Fetched ${allDelayedPrescriptions.length} delayed prescriptions for chunk`);

					// Get failed voucher orders for all users in chunk
					const allFailedVoucherOrders = allVoucherOrders.filter(o => ['FAILED', 'COLLECTIONS'].includes(o.status));

					for (const user of list) {
						const userId = user.id;
						//if (user.totalSubscriptions > 0) {

						// Filter data for this specific user
						const userSubscriptions = allSubscriptions.filter(s => s.userId === userId);
						const userVoucherOrders = allVoucherOrders.filter(o => o.userId === userId);
						const userMissedEncounters = allMissedEncounters.filter(e => e.userId === userId);
						const userDelayedPrescriptions = allDelayedPrescriptions.filter(o => o.userId === userId);
						const userFailedVoucherOrders = allFailedVoucherOrders.filter(o => o.userId === userId);

						// Calculate analytics
						const analytics = this.calculateUserAnalytics(
							user,
							userSubscriptions,
							userVoucherOrders,
							userMissedEncounters,
							userDelayedPrescriptions,
							userFailedVoucherOrders,
							programs
						);

						// Only update if there are changes
						if (Object.keys(analytics).length > 0) {
							const userRef = doc(collection(db, User.collectionName), userId);
							batch.update(userRef, analytics);
							batchUpdates++;
						}

						totalProcessed++;
						//}
					}

					// Commit the batch if there are updates
					if (batchUpdates > 0) {
						await batch.commit();
						totalUpdated += batchUpdates;
						console.log(`Updated ${batchUpdates} users in chunk ${page}`);
					}

					// console.log('go to next page');
					next = list[list.length - 1];
				}
			} catch (error) {
				console.log(error);
				throw error;
			}

			console.log(`Analytics backfill completed. Processed: ${totalProcessed}, Updated: ${totalUpdated}`);

			return {
				success: true,
				totalProcessed: totalProcessed,
				totalUpdated: totalUpdated,
				message: `Successfully processed ${totalProcessed} users and updated ${totalUpdated} users with new analytics`
			};

		} catch (error) {
			console.error('Error during analytics backfill:', error);
			return {
				success: false,
				error: error.message,
				message: 'Analytics backfill failed'
			};
		}
	}

	static backfillSubscriptionAnalytics = async () => {
		console.log('Starting comprehensive analytics backfill for all users...');

		let totalProcessed = 0;
		let totalUpdated = 0;

		try {
			try {
				let allSubscriptions = await Subscription.getAll();
				let userIds = allSubscriptions.map(s => s.userId);
				let users = await User.getByIds(userIds);

				let totalCount = users.length;
				let totalPages = Math.ceil(totalCount / 500.0);
				console.log('totalCount: ' + totalCount);
				console.log('totalPages: ' + totalPages);
				let pages = [];
				for (let i = 0; i < totalPages; i++) {
					pages.push(i);
				}

				const chunkedArray = chunkArray(users, 500);

				for (const page of pages) {
					console.log('starting page ' + page);

					let list = chunkedArray[page];
					console.log(`Fetched ${list.length} users for chunk`);

					const batch = writeBatch(db);
					let batchUpdates = 0;

					for (const user of list) {
						const userRef = doc(collection(db, User.collectionName), user.id);
						batch.update(userRef, { totalSubscriptions: allSubscriptions.filter(s => s.userId === user.id).length });
						batchUpdates++;

						totalProcessed++;
					}

					// Commit the batch if there are updates
					if (batchUpdates > 0) {
						await batch.commit();
						totalUpdated += batchUpdates;
						console.log(`Updated ${batchUpdates} users in chunk ${page}`);
					}
				}
			} catch (error) {
				console.log(error);
				throw error;
			}

			console.log(`Analytics backfill completed. Processed: ${totalProcessed}, Updated: ${totalUpdated}`);

			return {
				success: true,
				totalProcessed: totalProcessed,
				totalUpdated: totalUpdated,
				message: `Successfully processed ${totalProcessed} users and updated ${totalUpdated} users with new analytics`
			};
		} catch (error) {
			console.error('Error during analytics backfill:', error);
			return {
				success: false,
				error: error.message,
				message: 'Analytics backfill failed'
			};
		}
	}

	// Helper method to calculate all user analytics
	static calculateUserAnalytics = (user, subscriptions, voucherOrders, missedEncounters, delayedPrescriptions, failedVoucherOrders, programs) => {
		const updatedFields = {};

		// Subscription Analytics
		const activeSubscriptionStatuses = ['ACTIVE', 'PHARMACY_CHANGED'];
		// DENIED waits on customer service to make it ACTIVE again or cancel it, so it counts as pending
		const pendingSubscriptionStatuses = ['PENDING', 'DENIED'];
		const onHoldSubscriptionStatuses = ['ON_HOLD', 'PAUSED'];
		const cancelledSubscriptionStatuses = ['PENDING_CANCEL', 'CANCELLED', 'EXPIRED', 'SUSPENDED'];

		const activeSubscriptions = subscriptions.filter(s => activeSubscriptionStatuses.includes(s.status)).length;
		const pendingSubscriptions = subscriptions.filter(s => pendingSubscriptionStatuses.includes(s.status)).length;
		const onHoldSubscriptions = subscriptions.filter(s => onHoldSubscriptionStatuses.includes(s.status)).length;
		const cancelledSubscriptions = subscriptions.filter(s => cancelledSubscriptionStatuses.includes(s.status)).length;
		const totalSubscriptions = subscriptions.length;

		// Program Analytics
		const toProgramSet = (statuses) => [...new Set(subscriptions
			.filter(s => statuses.includes(s.status) && s.programId)
			.map(s => getProgramSlugById(s.programId, programs))
			.filter(slug => slug))];

		const activePrograms = toProgramSet(activeSubscriptionStatuses);
		const pendingPrograms = toProgramSet(pendingSubscriptionStatuses);
		const onHoldPrograms = toProgramSet(onHoldSubscriptionStatuses);
		const cancelledPrograms = toProgramSet(cancelledSubscriptionStatuses)
			.filter(slug => !activePrograms.includes(slug) && !pendingPrograms.includes(slug) && !onHoldPrograms.includes(slug));

		// Product Analytics (from subscription line items)
		const toProductSet = (statuses) => [...new Set(subscriptions
			.filter(s => statuses.includes(s.status))
			.flatMap(s => s.lineItems || [])
			.map(item => item.sku)
			.filter(sku => sku))];

		const activeProducts = toProductSet(activeSubscriptionStatuses);
		const pendingProducts = toProductSet(pendingSubscriptionStatuses);
		const onHoldProducts = toProductSet(onHoldSubscriptionStatuses);
		const cancelledProducts = toProductSet(cancelledSubscriptionStatuses)
			.filter(sku => !activeProducts.includes(sku) && !pendingProducts.includes(sku) && !onHoldProducts.includes(sku));

		// Order Analytics (based on voucher orders which represent actual payments)
		const completedVoucherOrders = voucherOrders.filter(o => o.isPaid);
		const orderedCompletedVoucherOrders = lodashOrderBy(completedVoucherOrders, ['orderedAt'], ['asc']);
		const totalLifetimeValue = completedVoucherOrders.reduce((sum, order) => sum + (order.paymentAmount || 0), 0);
		const totalOrderCount = completedVoucherOrders.length;
		const averageOrderValue = totalOrderCount > 0 ? totalLifetimeValue / totalOrderCount : 0;

		// Date Analytics
		const firstOrderDate = orderedCompletedVoucherOrders.length > 0 ? orderedCompletedVoucherOrders?.[0]?.orderedAt : null;
		const lastOrderDate = orderedCompletedVoucherOrders.length > 0 ? orderedCompletedVoucherOrders?.[orderedCompletedVoucherOrders.length - 1]?.orderedAt : null;

		// Customer since date (earliest of first order or first subscription)
		const orderedSubscriptions = lodashOrderBy(subscriptions, ['createdAt'], ['asc']);
		const earliestSubscriptionDate = orderedSubscriptions.length > 0 ? orderedSubscriptions?.[0]?.createdAt : null;
		const customerSince = earliestSubscriptionDate;

		// Calculated date fields
		const daysSinceFirstOrder = firstOrderDate ? this.calculateDaysSinceDate(firstOrderDate) : null;
		const daysSinceLastOrder = lastOrderDate ? this.calculateDaysSinceDate(lastOrderDate) : null;
		const customerSinceDays = customerSince ? this.calculateDaysSinceDate(customerSince) : null;

		// Status flags
		const hasPastDuePayments = failedVoucherOrders.length > 0;
		const hasMissedEncounters = missedEncounters.length > 0;
		const hasDelayedShipments = delayedPrescriptions.length > 0;

		// Churn risk calculation
		const churnRisk = this.calculateChurnRisk({
			...user,
			daysSinceLastOrder: daysSinceLastOrder,
			activeSubscriptions: activeSubscriptions,
			engagementScore: 0
		});

		// Customer segmentation based on LTV and activity
		let customerSegment = 'STANDARD';
		if ((totalLifetimeValue > 2000 && activeSubscriptions > 0) || activeSubscriptions > 2) {
			customerSegment = 'HIGH_VALUE';
		} else if ((totalLifetimeValue > 1000 && activeSubscriptions > 0) || activeSubscriptions > 1) {
			customerSegment = 'VIP';
		} else if (totalSubscriptions === 0) {
			customerSegment = 'LEAD';
		} else if (churnRisk === 'HIGH' && activeSubscriptions > 0) {
			customerSegment = 'AT_RISK';
		} else if (totalSubscriptions > 0 && activeSubscriptions === 0) {
			customerSegment = 'LOST';
		}

		// Only add fields that have changed
		updatedFields.totalSubscriptions = totalSubscriptions;
		updatedFields.activeSubscriptions = activeSubscriptions;
		updatedFields.pendingSubscriptions = pendingSubscriptions;
		updatedFields.onHoldSubscriptions = onHoldSubscriptions;
		updatedFields.cancelledSubscriptions = cancelledSubscriptions;

		// Compare arrays properly
		updatedFields.activePrograms = activePrograms;
		updatedFields.pendingPrograms = pendingPrograms;
		updatedFields.onHoldPrograms = onHoldPrograms;
		updatedFields.cancelledPrograms = cancelledPrograms;
		updatedFields.activeProducts = activeProducts;
		updatedFields.pendingProducts = pendingProducts;
		updatedFields.onHoldProducts = onHoldProducts;
		updatedFields.cancelledProducts = cancelledProducts;

		updatedFields.totalLifetimeValue = Math.round(totalLifetimeValue * 100) / 100;
		updatedFields.totalOrderCount = totalOrderCount;
		updatedFields.averageOrderValue = Math.round(averageOrderValue * 100) / 100;

		// Date fields
		updatedFields.firstOrderDate = firstOrderDate;
		updatedFields.lastOrderDate = lastOrderDate;
		updatedFields.customerSince = customerSince;

		// Calculated fields
		updatedFields.daysSinceFirstOrder = daysSinceFirstOrder;
		updatedFields.daysSinceLastOrder = daysSinceLastOrder;
		updatedFields.customerSinceDays = customerSinceDays;

		// Status flags
		updatedFields.hasPastDuePayments = hasPastDuePayments;
		updatedFields.hasMissedEncounters = hasMissedEncounters;
		updatedFields.hasDelayedShipments = hasDelayedShipments;

		// Risk and segmentation
		updatedFields.churnRisk = churnRisk;
		updatedFields.customerSegment = customerSegment;

		return updatedFields;
	}
}

const chunkArray = (arr, chunkSize) => {
	return Array.from({ length: Math.ceil(arr.length / chunkSize) }, (v, i) =>
		arr.slice(i * chunkSize, i * chunkSize + chunkSize)
	);
};

const addInsuranceRecordToBottom = (newRecord, oldList) => {
	// check to see if record already exists in the old list, update if so, insert if not
	let existingList = isListNotEmpty(oldList) ? [...oldList] : [];
	const index = existingList.findIndex(obj => obj['id'] === newRecord['id']);
	let updatedRecords = [];
	// only update if the record exists in the list
	if (index !== -1) {
		updatedRecords = [
			...existingList.slice(0, index), // everything before current obj
			newRecord,
			...existingList.slice(index + 1), // everything after current obj
		];
	} else {
		updatedRecords = [...existingList, newRecord];
	}

	return updatedRecords;
};

export default User;
